import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Copy, Trash, MapPin, SignOut, ArrowRight, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, adminLogout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function AdminDashboard() {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminLogout();
        navigate("/");
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClient = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/clients", { name: name.trim(), notes });
      toast.success("Client created");
      setOpen(false);
      setName("");
      setNotes("");
      navigate(`/admin/clients/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeClient = async (id, clientName) => {
    if (!window.confirm(`Delete tour for "${clientName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      setClients((c) => c.filter((x) => x.id !== id));
      toast.success("Client deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/t/${token}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Client link copied"))
      .catch(() => toast.info(url, { description: "Copy this link manually" }));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-8 py-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              INERRE bespoke
            </p>
            <h1 className="font-serif text-3xl font-light tracking-tighter">Showroom Tour</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/showrooms" data-testid="showroom-database-link">
              <Button variant="outline" className="rounded-full">
                <Storefront className="mr-2 h-4 w-4" /> Showroom Database
              </Button>
            </Link>
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={() => {
                adminLogout();
                navigate("/");
              }}
              className="rounded-full text-muted-foreground transition-colors duration-200 hover:text-foreground"
            >
              <SignOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-8 py-12">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-serif text-4xl font-light tracking-tighter">Client Tours</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Each client gets a private link with their showroom route and checklist.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-client-button"
                className="rounded-full bg-primary px-6 text-primary-foreground transition-transform duration-200 hover:scale-[1.03] hover:bg-primary"
              >
                <Plus className="mr-2 h-4 w-4" /> New Client
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl" data-testid="new-client-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl font-light tracking-tighter">
                  New Client Tour
                </DialogTitle>
                <DialogDescription>
                  Create a private showroom route and checklist link for a client.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createClient} className="space-y-5 pt-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Client name
                  </label>
                  <Input
                    data-testid="client-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Mr. & Mrs. Hartono"
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Notes (optional)
                  </label>
                  <Textarea
                    data-testid="client-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Project context, style direction…"
                    className="mt-2 rounded-xl"
                  />
                </div>
                <Button
                  data-testid="create-client-submit"
                  type="submit"
                  disabled={saving || !name.trim()}
                  className="h-11 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary"
                >
                  {saving ? "Creating…" : "Create Tour"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-10 space-y-4">
          {clients === null ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : clients.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-16 text-center">
              <MapPin className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-4 font-serif text-2xl font-light">No client tours yet</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Create your first client to start building a showroom route.
              </p>
            </div>
          ) : (
            clients.map((c, i) => (
              <motion.div
                key={c.id}
                data-testid={`client-card-${c.id}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-primary/40"
              >
                <Link to={`/admin/clients/${c.id}`} className="min-w-0 flex-1" data-testid={`client-open-${c.id}`}>
                  <h3 className="truncate font-serif text-2xl font-light tracking-tight">
                    {c.name}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {c.showroom_count} showroom{c.showroom_count === 1 ? "" : "s"}
                    {c.notes ? ` · ${c.notes}` : ""}
                  </p>
                </Link>
                <div className="ml-6 flex items-center gap-2">
                  <Button
                    data-testid={`copy-link-${c.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => copyLink(c.token)}
                    className="rounded-full"
                  >
                    <Copy className="mr-2 h-3.5 w-3.5" /> Copy link
                  </Button>
                  <Button
                    data-testid={`delete-client-${c.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => removeClient(c.id, c.name)}
                    className="rounded-full text-muted-foreground transition-colors duration-200 hover:text-destructive"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                  <Link to={`/admin/clients/${c.id}`}>
                    <ArrowRight className="h-5 w-5 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
